import { useFormContext } from 'react-hook-form';
import { Contract, ENewOrUpdatePrices, EPageViewType } from '../../types';
import React from 'react';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { FormCard } from '@portal_components/components/FormCard';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { FormTextInput } from '@portal_components/components/FormTextInput';

interface ContractDetailsTabProps {
  readonly: boolean;
  viewType: EPageViewType;
}
export const ContractDetailsTab = ({ readonly, viewType }: ContractDetailsTabProps) => {
  const { watch } = useFormContext<Contract>();

  const {
    standingData: { standingDataAssetOwnershipList, standingDataContractTypeList, standingDataEnergyManagementTypeList }
  } = useContractStandingData();

  const [updateOrNewPrices, upliftTypeId, upliftDate, dateChange, currentlyFutureDatedContract] = watch([
    'updateOrNewPrices',
    'upliftTypeId',
    'upliftDate',
    'dateChange',
    'currentlyFutureDatedContract'
  ]);

  const hasUplift = React.useMemo(() => {
    return !!upliftTypeId || !!upliftDate;
  }, [upliftDate, upliftTypeId]);

  return (
    <div className="flex flex-col w-full">
      {[EPageViewType.EDIT].includes(viewType) && (
        <FormCard columns={2}>
          <FormRadioButtonGroup<Contract>
            title="Price Update Type"
            mandatory
            buttons={Object.values(ENewOrUpdatePrices).map(value => ({ title: value, value }))}
            name="updateOrNewPrices"
            readonly={(dateChange || currentlyFutureDatedContract) ?? false}
          />
          <br />
          {updateOrNewPrices === ENewOrUpdatePrices.NEW && <FormDatePicker<Contract> title="Price Start Date" name="contractPriceStartDate" mandatory />}
          {updateOrNewPrices === ENewOrUpdatePrices.NEW && <FormDatePicker<Contract> title="Price End Date" name="contractPriceEndDate" readonly />}
        </FormCard>
      )}
      <FormCard columns={2}>
        <FormTextInput<Contract> title="Reference Number" name="contractReference" readonly={readonly} />
        <FormDropdown<Contract>
          title="Business Area"
          readonly={viewType !== EPageViewType.ADD}
          placeholder="Select..."
          name="businessAreaId"
          elementsForDisplay={[{ title: 'Commercial and Industrial', value: 'CI' }]}
          mandatory
        />
        <FormDropdown<Contract>
          title="Contract Type"
          readonly={viewType !== EPageViewType.ADD}
          placeholder="Select..."
          mandatory
          name="contractType"
          elementsForDisplay={standingDataContractTypeList}
        />
        <FormTextInput<Contract> title="Assigned Account Manager" name="accountManager" mandatory readonly={readonly} />
        <FormDatePicker<Contract> title="Contract Start Date" name="contractStartDate" mandatory readonly={readonly} />
        <FormDatePicker<Contract> title="Contract End Date" name="contractEndDate" mandatory readonly={![EPageViewType.ADD, EPageViewType.RENEW].includes(viewType)} />
        <FormDropdown<Contract> title="Energy Management Type" placeholder="Select..." elementsForDisplay={standingDataEnergyManagementTypeList} name="energyManagementType" readonly={readonly} />
        <FormTextInput<Contract> title="Supplier Name" name="supplier" readonly={viewType !== EPageViewType.ADD} />
      </FormCard>
      <FormCard columns={2}>
        <FormRadioButtonGroup<Contract>
          title="Letter of Authority"
          mandatory
          name="letterOfAuthority"
          buttons={[
            { title: 'Yes', value: 'Y' },
            { title: 'No', value: 'N' }
          ]}
          readonly={readonly}
        />
        <FormDropdown<Contract> title="Asset Ownership" name="assetOwnership" mandatory readonly={readonly} placeholder="Select..." elementsForDisplay={standingDataAssetOwnershipList} />
        <FormRadioButtonGroup<Contract>
          title="Evergreen"
          name="evergreen"
          buttons={[
            { title: 'Yes', value: 'Y' },
            { title: 'No', value: 'N' }
          ]}
          mandatory
          readonly={readonly}
        />
      </FormCard>
      <FormCard columns={2}>
        <FormRadioButtonGroup<Contract>
          title="Retail Price Index / Consumer Price Index"
          name="upliftTypeId"
          mandatory={hasUplift}
          buttons={[
            { title: 'RPI', value: '1' },
            { title: 'CPI', value: '2' }
          ]}
          readonly={readonly}
        />
        <FormDatePicker<Contract> title="Uplift Date" name="upliftDate" mandatory={hasUplift} readonly={readonly} />
      </FormCard>
    </div>
  );
};
